import { useEffect, useState } from "react";
import { Button, LoadingCard, showToast } from "@xpense/ui";
import { api, Transaction } from "../api/client";
import { EmptyState, MetricCard, Page, RetryButton, Section } from "../components/Page";
import { Icon } from "../components/Icons";
import TransactionCard from "../components/TransactionCard";
import EditTransactionModal from "../components/EditTransactionModal";
import ConfirmDeleteModal from "../components/ConfirmDeleteModal";

export default function ReviewQueue() {
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [editingTxn, setEditingTxn] = useState<Transaction | null>(null);
  const [deletingTxn, setDeletingTxn] = useState<Transaction | null>(null);

  useEffect(() => {
    loadReviewQueue();
  }, []);

  const loadReviewQueue = async () => {
    setLoading(true);
    setError("");
    try {
      const response = await api.get<{ data: Transaction[] }>("/api/v1/review-queue");
      setTransactions(response.data);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load review queue");
    } finally {
      setLoading(false);
    }
  };

  const handleConfirm = async (id: string) => {
    try {
      await api.post(`/api/v1/review-queue/${id}/confirm`);
      setTransactions((txns) => txns.filter((t) => t.id !== id));
      showToast("Transaction confirmed successfully", "success");
    } catch (err) {
      showToast(err instanceof Error ? err.message : "Failed to confirm transaction", "error");
    }
  };

  if (loading) {
    return (
      <Page
        eyebrow="Human-in-the-loop"
        title="Review Queue"
        description="Low-confidence classifications appear here before they affect downstream insights."
      >
        <div className="xpn-grid xpn-grid--3">
          <LoadingCard />
          <LoadingCard />
          <LoadingCard />
        </div>
      </Page>
    );
  }

  if (error) {
    return (
      <Page eyebrow="Human-in-the-loop" title="Review Queue">
        <Section>
          <EmptyState
            icon="warning"
            title="Unable to load review queue"
            description={error}
            action={<RetryButton onClick={loadReviewQueue} />}
            tone="danger"
          />
        </Section>
      </Page>
    );
  }

  return (
    <Page
      eyebrow="Human-in-the-loop"
      title="Review Queue"
      description="Confirm uncertain transactions to keep your model-backed ledger trustworthy."
      action={
        <Button variant="secondary" size="sm" onClick={loadReviewQueue}>
          <Icon name="refresh" />
          Refresh
        </Button>
      }
    >
      <div className="xpn-grid xpn-grid--3">
        <MetricCard
          label="Needs review"
          value={transactions.length}
          caption="Pending human confirmation"
          tone={transactions.length > 0 ? "warning" : "success"}
        />
        <MetricCard label="Threshold" value="70%" caption="Default confidence gate" tone="info" />
        <MetricCard label="Action" value="Confirm" caption="Moves items to confirmed status" />
      </div>

      <Section title="Queued items" description="Review amount, merchant, category, and raw event before confirming.">
        {transactions.length === 0 ? (
          <EmptyState
            icon="check"
            title="No transactions need review"
            description="Everything is caught up. New low-confidence classifications will land here."
            tone="success"
          />
        ) : (
          <div className="xpn-row-list">
            {transactions.map((txn) => (
              <div key={txn.id}>
                <TransactionCard transaction={txn} />
                <div
                  className="xpn-flex-between"
                  style={{
                    padding: "0 var(--xpn-space-xl) var(--xpn-space-lg) calc(var(--xpn-space-xl) + 52px)",
                    gap: "var(--xpn-space-md)",
                    flexWrap: "wrap",
                  }}
                >
                  <span className="xpn-text-caption xpn-text-muted">
                    Confirming marks this classification as reviewed.
                  </span>
                  <div style={{ display: "flex", gap: "var(--xpn-space-sm)", flexWrap: "wrap" }}>
                    <Button variant="secondary" size="sm" onClick={() => setEditingTxn(txn)}>
                      Edit
                    </Button>
                    <Button variant="danger" size="sm" onClick={() => setDeletingTxn(txn)}>
                      <Icon name="trash" />
                      Delete
                    </Button>
                    <Button onClick={() => handleConfirm(txn.id)} size="sm">
                      <Icon name="check" />
                      Confirm
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </Section>

      {editingTxn && (
        <EditTransactionModal
          transaction={editingTxn}
          open={!!editingTxn}
          onClose={() => setEditingTxn(null)}
          onSaved={(updated) => {
            setTransactions((prev) => prev.map((t) => (t.id === updated.id ? updated : t)));
            setEditingTxn(null);
          }}
        />
      )}

      {deletingTxn && (
        <ConfirmDeleteModal
          transaction={deletingTxn}
          open={!!deletingTxn}
          onClose={() => setDeletingTxn(null)}
          onDeleted={(id) => {
            setTransactions((prev) => prev.filter((t) => t.id !== id));
            setDeletingTxn(null);
          }}
        />
      )}
    </Page>
  );
}
